import { type FC, type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useNoodleApi from '@/hooks/useNoodleApi';
import useFilingProgress from '@/lib/PacerSubmissionPane/useFilingProgress';
import { logError } from '@/providers/ErrorTracking';
import { SocketMessageType } from '@/providers/Socket/SocketContext';
import useSocketContext from '@/providers/Socket/useSocketContext';
import * as tsClient from '@/tsClient';

import FilingProgressContext, {
  type ActiveFiling,
  type FilingProgressEntry,
  type IFilingProgressContext,
} from './FilingProgressContext';

const FILING_CLOSE_REGEXP = /E-filing (succeeded|failed|cancelled)/;

type FilingEntryManagerProps = {
  filing: ActiveFiling;
  onStepsChange: (submissionAttemptId: string, update: Pick<FilingProgressEntry, 'isCancelling' | 'steps'>) => void;
  onFinished: (submissionAttemptId: string) => void;
  onCancelHandled: (submissionAttemptId: string) => void;
  cancelRequested: boolean;
};

/**
 * Renderless component that manages socket subscriptions and step progress for a single filing.
 * One instance per tracked filing — allows us to call hooks without violating rules of hooks.
 */
const FilingEntryManager: FC<FilingEntryManagerProps> = ({
  filing,
  onStepsChange,
  onFinished,
  onCancelHandled,
  cancelRequested,
}) => {
  const { submissionAttemptId } = filing;
  const { addListener, removeListener, isInitialized: isSocketInitialized, joinChannels } = useSocketContext();
  const { steps, isCancelling, processMessage, handleCancel, markFinished, setStepError } = useFilingProgress({
    submissionAttemptId,
  });

  const { getData: getSingleMessage } = useNoodleApi(tsClient.integrations.getCaseSubmissionMessage);
  const { getData: getSubmissionMessages } = useNoodleApi(tsClient.integrations.getCaseSubmissionMessages);
  const { getData: getLatestSubmission } = useNoodleApi(tsClient.integrations.getLatestCaseSubmission);

  // Sync steps and cancelling state up to the provider
  useEffect(() => {
    onStepsChange(submissionAttemptId, { steps, isCancelling });
  }, [steps, isCancelling, submissionAttemptId, onStepsChange]);

  // Handle cancel requests from the provider — clear the flag after handling so retries work
  const previousCancelRequested = useRef(false);
  useEffect(() => {
    if (cancelRequested && !previousCancelRequested.current) {
      void handleCancel().finally(() => {
        onCancelHandled(submissionAttemptId);
      });
    }
    previousCancelRequested.current = cancelRequested;
  }, [cancelRequested, handleCancel, onCancelHandled, submissionAttemptId]);

  // Join the submission attempt channel and replay historical messages
  useEffect(() => {
    if (!isSocketInitialized || !submissionAttemptId) return undefined;

    let cancelled = false;

    const subscribeAndReplay = async (): Promise<void> => {
      await joinChannels([`submission-attempts/${submissionAttemptId}`]);

      const { data: historicalMessages } = await getSubmissionMessages({
        id: submissionAttemptId,
        page: 1,
        perPage: 1_000,
      });
      if (cancelled) return;
      if (historicalMessages?.items) {
        [...historicalMessages.items].reverse().forEach((message) => {
          processMessage(message);
        });
      }
    };

    subscribeAndReplay().catch(logError);

    return () => {
      cancelled = true;
    };
  }, [isSocketInitialized, joinChannels, submissionAttemptId, getSubmissionMessages, processMessage]);

  // Listen for AI messages to populate step progress and detect completion
  useEffect(() => {
    if (!submissionAttemptId) return undefined;
    const listenerId = addListener({
      fn: async (data: unknown) => {
        const { aiMessageId, submissionAttemptId: messageAttemptId } = data as {
          aiMessageId: string;
          submissionAttemptId: string;
        };
        if (messageAttemptId !== submissionAttemptId) return;
        const { data: message } = await getSingleMessage({ id: submissionAttemptId, messageId: aiMessageId });
        if (message) {
          processMessage(message);
          if (message.text && FILING_CLOSE_REGEXP.test(message.text)) {
            // Filing completed — refetch to determine outcome
            const { data: submission } = await getLatestSubmission({
              provider: 'pacer',
              userWorkflowId: filing.userWorkflowId,
            });
            if (submission?.status && submission.status !== 'in-progress') {
              markFinished(submission.status);
              // Apply error context if the submission failed
              if (submission.status === 'failed') {
                const { parseSubmissionErrorContext } =
                  await import('@/tsClient/servers/governmentIntegrationsService/contract');
                const errorContext = parseSubmissionErrorContext(submission.submissionJson);
                if (errorContext?.step && errorContext.category) {
                  setStepError(errorContext.step, `An unexpected error occurred.`);
                }
              }
              onFinished(submissionAttemptId);
            }
          }
        }
      },
      messageType: SocketMessageType.AI_CASE_SUBMISSION_MESSAGE,
    });
    return () => {
      removeListener(listenerId);
    };
  }, [
    addListener,
    removeListener,
    getSingleMessage,
    getLatestSubmission,
    submissionAttemptId,
    processMessage,
    markFinished,
    setStepError,
    filing.userWorkflowId,
    onFinished,
  ]);

  return null;
};

/** Global provider that manages filing progress state across page navigations. */
const FilingProgressProvider: FC<PropsWithChildren> = ({ children }) => {
  const [entriesMap, setEntriesMap] = useState<Map<string, FilingProgressEntry>>(() => new Map());
  const [cancelRequests, setCancelRequests] = useState<Set<string>>(() => new Set());

  const registerFiling = useCallback((filing: ActiveFiling) => {
    setEntriesMap((previous) => {
      if (previous.has(filing.submissionAttemptId)) return previous;
      const next = new Map(previous);
      next.set(filing.submissionAttemptId, {
        userWorkflowId: filing.userWorkflowId,
        submissionAttemptId: filing.submissionAttemptId,
        steps: [],
        isCancelling: false,
        isFinished: false,
        isDismissed: false,
      });
      return next;
    });
  }, []);

  const unregisterFiling = useCallback((submissionAttemptId: string) => {
    setEntriesMap((previous) => {
      if (!previous.has(submissionAttemptId)) return previous;
      const next = new Map(previous);
      next.delete(submissionAttemptId);
      return next;
    });
  }, []);

  const dismissFiling = useCallback((submissionAttemptId: string) => {
    setEntriesMap((previous) => {
      const entry = previous.get(submissionAttemptId);
      if (!entry) return previous;
      const next = new Map(previous);
      next.set(submissionAttemptId, { ...entry, isDismissed: true });
      // Clean up if already finished
      if (entry.isFinished) {
        next.delete(submissionAttemptId);
      }
      return next;
    });
  }, []);

  const cancelFiling = useCallback((submissionAttemptId: string) => {
    setCancelRequests((previous) => {
      if (previous.has(submissionAttemptId)) return previous;
      const next = new Set(previous);
      next.add(submissionAttemptId);
      return next;
    });
  }, []);

  const handleStepsChange = useCallback(
    (submissionAttemptId: string, update: Pick<FilingProgressEntry, 'isCancelling' | 'steps'>) => {
      setEntriesMap((previous) => {
        const entry = previous.get(submissionAttemptId);
        if (!entry) return previous;
        const next = new Map(previous);
        next.set(submissionAttemptId, { ...entry, ...update });
        return next;
      });
    },
    [],
  );

  const handleFinished = useCallback((submissionAttemptId: string) => {
    setEntriesMap((previous) => {
      const entry = previous.get(submissionAttemptId);
      if (!entry) return previous;
      const next = new Map(previous);
      const updated = { ...entry, isFinished: true };
      // Clean up if already dismissed
      if (entry.isDismissed) {
        next.delete(submissionAttemptId);
      } else {
        next.set(submissionAttemptId, updated);
      }
      return next;
    });
    setCancelRequests((previous) => {
      if (!previous.has(submissionAttemptId)) return previous;
      const next = new Set(previous);
      next.delete(submissionAttemptId);
      return next;
    });
  }, []);

  /** Clear a cancel request after the manager has handled it, allowing future retries. */
  const handleCancelHandled = useCallback((submissionAttemptId: string) => {
    setCancelRequests((previous) => {
      if (!previous.has(submissionAttemptId)) return previous;
      const next = new Set(previous);
      next.delete(submissionAttemptId);
      return next;
    });
  }, []);

  const filings = useMemo(() => Array.from(entriesMap.values()), [entriesMap]);

  /** Filings that still need a socket manager — excluded only once both dismissed and finished. */
  const managedFilings = useMemo(() => filings.filter((entry) => !entry.isDismissed || !entry.isFinished), [filings]);

  const contextValue = useMemo<IFilingProgressContext>(
    () => ({
      registerFiling,
      unregisterFiling,
      dismissFiling,
      cancelFiling,
      filings,
    }),
    [registerFiling, unregisterFiling, dismissFiling, cancelFiling, filings],
  );

  return (
    <FilingProgressContext.Provider value={contextValue}>
      {managedFilings.map((entry) => (
        <FilingEntryManager
          key={entry.submissionAttemptId}
          filing={{ userWorkflowId: entry.userWorkflowId, submissionAttemptId: entry.submissionAttemptId }}
          onStepsChange={handleStepsChange}
          onFinished={handleFinished}
          onCancelHandled={handleCancelHandled}
          cancelRequested={cancelRequests.has(entry.submissionAttemptId)}
        />
      ))}
      {children}
    </FilingProgressContext.Provider>
  );
};

export default FilingProgressProvider;
